/**
 * Tenant-aware Link component
 *
 * Automatically preserves the tenant query parameter in URLs
 * when running in localhost development mode.
 */

import { Link, LinkProps } from "wouter";
import { tenantHref } from "@/hooks/useTenantNavigation";
import { forwardRef } from "react";

interface TenantLinkProps extends Omit<LinkProps, "href"> {
  href: string;
  children: React.ReactNode;
}

/**
 * A Link component that preserves the tenant query parameter.
 *
 * Usage:
 *   <TenantLink href="/app/agents">Agents</TenantLink>
 *
 * In localhost with ?tenant=elite, this will navigate to:
 *   /app/agents?tenant=elite
 */
export const TenantLink = forwardRef<HTMLAnchorElement, TenantLinkProps>(
  ({ href, children, ...props }, ref) => {
    const tenantAwareHref = tenantHref(href);

    return (
      <Link href={tenantAwareHref} {...props} ref={ref}>
        {children}
      </Link>
    );
  }
);

TenantLink.displayName = "TenantLink";

export default TenantLink;
